import axios from 'axios';
import type {
  PasswordResetRequestDto,
  PasswordResetVerifyResponse,
  PasswordResetConfirmRequest,
} from '../types/passwordReset.types';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
// Password reset is a public flow but we still attach the interceptor so
// any unexpected 401 (e.g. expired confirm token treated as session expiry
// by the BE) is handled consistently — the bypass paths (/auth/*) cover
// the auth endpoints; password reset endpoints are /password-reset/*.
attachAuthInterceptors(apiClient);

/** Attach bearer token if present */
apiClient.interceptors.request.use((config) => {
  const token =
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/** Unwrap ApiResponse<T> or reject with structured error */
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId =
      (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const passwordResetApi = {
  /**
   * POST /api/eraf/auth/password-reset/request
   * Always returns 200 regardless of email existence (info-disclosure prevention).
   */
  request: (dto: PasswordResetRequestDto): Promise<void> =>
    apiClient
      .post<void>('/auth/password-reset/request', dto)
      .then(() => undefined),

  /**
   * GET /api/eraf/auth/password-reset/verify?token=...
   * Returns PasswordResetVerifyResponse with valid/expiresAt/maskedEmail.
   */
  verify: (token: string): Promise<PasswordResetVerifyResponse> =>
    apiClient
      .get<unknown, { data: PasswordResetVerifyResponse }>(
        `/auth/password-reset/verify?token=${encodeURIComponent(token)}`
      )
      .then((res) => res.data),

  /**
   * POST /api/eraf/auth/password-reset/confirm
   * Validates token + sets new password. Token is single-use.
   */
  confirm: (dto: PasswordResetConfirmRequest): Promise<void> =>
    apiClient
      .post<void>('/auth/password-reset/confirm', dto)
      .then(() => undefined),
};
